import { MenuItemType } from "./ActionsPopover.types";
import RowMenu from "./RowMenu";
import { Popover, useTheme } from "@mui/material";
import { useRef } from "react";

export type ActionsPopoverAppearance = "legacy" | "themed";

type ActionsPopoverProps = {
    menuItems: MenuItemType[];
    anchor: HTMLElement | null;
    closeWindow: () => void;
    appearance?: ActionsPopoverAppearance;
};

const ARROW_SIZE = 15;

const ActionsPopover = ({ menuItems, anchor, closeWindow, appearance = "legacy" }: Readonly<ActionsPopoverProps>) => {
    const isRowMenuOpen = Boolean(anchor);
    const theme = useTheme();
    const paperPositionObserver = useRef<MutationObserver | null>(null);

    const filteredMenuItems = menuItems.flatMap((menuItem) => {
        const shouldShow = menuItem.shouldShow?.();
        if (shouldShow === true || shouldShow === undefined) {
            return [menuItem];
        }

        return [];
    });

    const pointArrowAtAnchor = (paper: HTMLElement) => {
        if (anchor === null) return;
        const { left, width } = anchor.getBoundingClientRect();
        const arrowLeft = `${left + width / 2 - paper.offsetLeft - paper.clientLeft - ARROW_SIZE}px`;
        if (paper.style.getPropertyValue("--arrow-left") !== arrowLeft) {
            paper.style.setProperty("--arrow-left", arrowLeft);
        }
    };

    const followPaperPosition = (paper: HTMLElement) => {
        paperPositionObserver.current?.disconnect();
        paperPositionObserver.current = new MutationObserver(() => pointArrowAtAnchor(paper));
        paperPositionObserver.current.observe(paper, { attributeFilter: ["style"] });
    };

    const stopFollowingPaperPosition = () => paperPositionObserver.current?.disconnect();

    const arrow = {
        content: '""',
        display: "block",
        position: "absolute",
        left: "var(--arrow-left)",
        width: 0,
        height: 0,
        borderLeft: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid transparent`,
    };

    return (
        <Popover
            anchorEl={anchor}
            open={isRowMenuOpen}
            onClose={closeWindow}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            slotProps={{
                transition: { onEntering: followPaperPosition, onExited: stopFollowingPaperPosition },
                paper: {
                    sx: {
                        padding: appearance === "themed" ? theme.spacing(3, 2.5) : theme.spacing(2, 1.5),
                        borderRadius: theme.spacing(1.5),
                        marginTop: theme.spacing(1.5),
                        overflow: "visible",
                        boxShadow: theme.custom.shadows.popover,
                        border: `1px solid ${theme.custom.grey[500]}`,
                        "&:before": {
                            ...arrow,
                            top: -ARROW_SIZE,
                            borderBottom: `${ARROW_SIZE}px solid ${theme.custom.grey[500]}`,
                            zIndex: -1,
                        },
                        "&:after": {
                            ...arrow,
                            top: -(ARROW_SIZE - 1),
                            borderBottom: `${ARROW_SIZE}px solid ${theme.palette.background.paper}`,
                            zIndex: 0,
                        },
                    },
                },
            }}
        >
            <RowMenu appearance={appearance} menuItems={filteredMenuItems} />
        </Popover>
    );
};

export default ActionsPopover;
